import { useCallback, useEffect, useRef, useState } from 'react';
import type { tableSettingsProps } from 'src/core/tables/useTableSettings';
import { useMessageContext } from 'src/shared/support/MessageContext';
import { CollectivePickingListPrintJob, PickingListPrintJob, useDispatchPrintingSettings } from 'src/shared/printing/PrintingManager';
import type { PickingListPrintResult } from 'src/shared/printing/PrintingManager';

export const useDispatchOrderPrinting = (warehouseId: number | string | null, onPrinted?: () => void) => {
    const printingSettings = useDispatchPrintingSettings();
    const [autoPrintEnabled, setAutoPrintEnabled] = useState(false);
    const [printJob, setPrintJob] = useState<PrintJob | null>(null);
    const { updateMessageStatus } = useMessageContext();
    const reportPrint = useCallback((result: PickingListPrintResult) => {
        if (result.error) updateMessageStatus({ requestUrl: '/printing/picking-list', status: 'error', message: result.error });
        else if (result.outcome && result.outcome.method !== 'printer') updateMessageStatus({ requestUrl: '/printing/picking-list', status: 'success', message: `${result.outcome.method === 'savePdf' ? 'PDF download requested.' : 'Printing skipped.'}${result.outcome.reason ? ` ${result.outcome.reason}` : ''}` });
    }, [updateMessageStatus]);
    const printSequence = useRef(0);
    const printingRef = useRef(false);
    const [autoPrintPendingOrderId, setAutoPrintPendingOrderId] = useState<number | string | null>(null);
    const printing = printJob != null;
    const autoPrintOrderIds = useRef<Set<string>>(new Set());
    const startPrint = useCallback((orderId: number | string) => {
        if (warehouseId == null || orderId == null || printingRef.current) return;
        printingRef.current = true;
        autoPrintOrderIds.current.add(`${warehouseId}:${orderId}`);
        setPrintJob({ type: 'single', key: ++printSequence.current, orderId, warehouseId, settings: printingSettings });
    }, [warehouseId, printingSettings]);

    const startCollectivePrint = useCallback((pickingListIds: string[]) => {
        if (warehouseId == null || printingRef.current) return;

        if (!pickingListIds.length) {
            reportPrint({ error: 'Select at least one order with a picking list.' });
            return;
        }

        printingRef.current = true;
        setAutoPrintPendingOrderId(null);
        setPrintJob({ type: 'collective', key: ++printSequence.current, pickingListIds: pickingListIds.join(','), warehouseId, settings: printingSettings });
    }, [warehouseId, printingSettings, reportPrint]);

    const finishPrint = useCallback((result: PickingListPrintResult) => {
        printingRef.current = false;
        reportPrint(result);
        setPrintJob(null);
        onPrinted?.();
    }, [reportPrint, onPrinted]);

    return { printJob, printing, printingSettings, autoPrintEnabled, setAutoPrintEnabled, autoPrintPendingOrderId, setAutoPrintPendingOrderId, autoPrintOrderIds, startPrint, startCollectivePrint, finishPrint };
};

export const useDispatchPrintQueue = ({ warehouseId, tableSettings, activeRows, includeArchived, printing, onRefresh }: { warehouseId: number | string | null; tableSettings: tableSettingsProps; activeRows: any[]; includeArchived: boolean; printing: ReturnType<typeof useDispatchOrderPrinting>; onRefresh: () => void }) => {
    const { autoPrintEnabled, autoPrintPendingOrderId, setAutoPrintPendingOrderId, autoPrintOrderIds, printingSettings, startPrint } = printing;
    const printingBusy = printing.printing;
    const [selectedPickingListCount, setSelectedPickingListCount] = useState(0);
    const apiRef = tableSettings.apiRef;

    const getSelectedPickingListIds = useCallback(() => {
        const selectedRows = apiRef.current?.getSelectedRows();
        if (!selectedRows || warehouseId == null) return [];

        return Array.from(new Set(
            Array.from(selectedRows.values())
                .filter((row) => hasPickingListId(row) && (row.WarehouseId == null || String(row.WarehouseId) === String(warehouseId)))
                .map((row) => String(row.PickingListId))
        ));
    }, [apiRef, warehouseId]);

    useEffect(() => {
        const updateSelection = () => setSelectedPickingListCount(getSelectedPickingListIds().length);
        updateSelection();
        return apiRef.current?.subscribeEvent('rowSelectionChange', updateSelection);
    }, [apiRef, getSelectedPickingListIds, tableSettings.allRows, includeArchived]);

    useEffect(() => {
        if (warehouseId == null) return;
        const interval = window.setInterval(() => onRefresh(), printingSettings.pickingList.refreshMs);
        return () => window.clearInterval(interval);
    }, [warehouseId, printingSettings.pickingList.refreshMs, onRefresh]);

    useEffect(() => {
        setAutoPrintPendingOrderId(null);
    }, [warehouseId]);

    const printableRows = (activeRows ?? []).filter((row) => row.DispatchOrderId != null &&
        row.PickingListId == null &&
        row.DispatchOrderBlockId == null &&
        !autoPrintOrderIds.current.has(`${warehouseId}:${row.DispatchOrderId}`)
    );

    const autoPrintQueueLength = printableRows.length;
    const autoPrintStatus = !autoPrintEnabled ? 'Automatic printing off'
        : printingBusy ? `Printing • ${autoPrintQueueLength} queued`
            : autoPrintPendingOrderId != null ? `Next print in ${printingSettings.pickingList.delayMs / 1000}s • ${autoPrintQueueLength} queued`
                : autoPrintQueueLength > 0 ? `${autoPrintQueueLength} queued`
                    : 'No printing left';

    useEffect(() => {
        if (!autoPrintEnabled || warehouseId == null || printingBusy || autoPrintPendingOrderId != null) return;

        const nextRow = [...(activeRows ?? [])].reverse().find((row) => row.DispatchOrderId != null &&
            row.PickingListId == null &&
            row.DispatchOrderBlockId == null &&
            !autoPrintOrderIds.current.has(`${warehouseId}:${row.DispatchOrderId}`)
        );

        if (!nextRow) return;
        setAutoPrintPendingOrderId(nextRow.DispatchOrderId);
    }, [autoPrintEnabled, warehouseId, activeRows, printingBusy, autoPrintPendingOrderId]);

    useEffect(() => {
        if (autoPrintPendingOrderId == null) return;

        if (!autoPrintEnabled || warehouseId == null) {
            setAutoPrintPendingOrderId(null);
            return;
        }

        if (printingBusy) return;

        const pendingRow = (activeRows ?? []).find((row) => row.DispatchOrderId === autoPrintPendingOrderId);
        if (!pendingRow || pendingRow.PickingListId != null || pendingRow.DispatchOrderBlockId != null) {
            setAutoPrintPendingOrderId(null);
            return;
        }

        const timeout = window.setTimeout(() => {
            startPrint(autoPrintPendingOrderId);
            setAutoPrintPendingOrderId(null);
        }, printingSettings.pickingList.delayMs);

        return () => window.clearTimeout(timeout);
    }, [autoPrintPendingOrderId, autoPrintEnabled, warehouseId, printingBusy, printingSettings, activeRows, startPrint]);

    return { selectedPickingListCount, startCollectivePrint: () => printing.startCollectivePrint(getSelectedPickingListIds()), autoPrintStatus };
};

export const DispatchPickingListJobs = ({ printing }: { printing: ReturnType<typeof useDispatchOrderPrinting> }) => {
    const { printJob, finishPrint } = printing;
    return <>
        {printJob?.type === 'single' && <PickingListPrintJob key={printJob.key} orderId={printJob.orderId} warehouseId={printJob.warehouseId} settings={printJob.settings} onComplete={finishPrint} />}
        {printJob?.type === 'collective' && <CollectivePickingListPrintJob key={printJob.key} pickingListIds={printJob.pickingListIds} warehouseId={printJob.warehouseId} settings={printJob.settings} onComplete={finishPrint} />}
    </>;
};

export const hasPickingListId = (row: { PickingListId?: string | number | null }) => row.PickingListId != null && String(row.PickingListId).trim() !== '';

type PrintJob = { key: number; warehouseId: number | string; settings: ReturnType<typeof useDispatchPrintingSettings> } & ({ type: 'single'; orderId: number | string } | { type: 'collective'; pickingListIds: string });
